import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import billingService from "../../services/billingService";
import SEO from "../../components/SEO/SEO";
import { FinalCtaCard } from "../../components/landing/PartnerAndCta";
import { PillarPills } from "../../components/landing/LaunchSections";
import MoveHero from "./components/MoveHero";
import MoveSearchBar, { MoveSubcategory } from "./components/MoveSearchBar";
import { MoveEcosystem, MoveVehicles, MoveWhy } from "./components/MoveIntroSections";
import { MoveDurations, MovePickup } from "./components/MoveFlexibility";
import MoveTrust from "./components/MoveTrust";
import MoveJourney from "./components/MoveJourney";
import { MoveComingSoon, MoveOnePlatform, MovePartner } from "./components/MoveClosingSections";
import logger from "../../utils/logger";
import "./index.css";

// Used when the registry has no MOVE category or subcategories yet (carried over)
const DEFAULT_MOVE_SUBCATEGORIES: MoveSubcategory[] = [
  { id: "201", name: "Book a Ride" },
  { id: "202", name: "Rent a Vehicle" },
  { id: "203", name: "Chauffeur Service" },
  { id: "204", name: "Fleet Subscription" },
];

const PILLAR_PILLS = [
  { label: "WORK", color: "#017bef" },
  { label: "STAY", color: "#0062c0" },
  { label: "PLAY", color: "#0ea5e9" },
  { label: "MOVE", color: "#012d6e" },
];

const findMoveCategory = (cats: any[]) =>
  cats.find(c => (c.code || "").toUpperCase() === "MOVE" || (c.name || "").toUpperCase().includes("MOVE"));

const toList = (res: any): any[] => (Array.isArray(res?.data) ? res.data : res?.data?.data || res?.data?.content || []);

export default function MoveLandingScreen() {
  const history = useHistory();
  const reduxCategories = useSelector((state: any) => state?.dashboard?.serviceCategories || []);
  const reduxSubcategories = useSelector((state: any) => state?.dashboard?.serviceSubcategories || []);
  const [moveCategoryId, setMoveCategoryId] = useState("");
  const [subcategories, setSubcategories] = useState<MoveSubcategory[]>([]);

  // Resolve the MOVE category from Redux, falling back to the public registry
  useEffect(() => {
    const cached = findMoveCategory(reduxCategories);
    if (cached) {
      setMoveCategoryId(String(cached.id));
      const subs = reduxSubcategories.filter((s: any) => String(s.categoryId) === String(cached.id));
      if (subs.length > 0) {
        setSubcategories(subs);
        return;
      }
    }

    billingService
      .getPublicServiceCategories({ orderBy: "name:desc" })
      .then((res: any) => {
        const moveCat = findMoveCategory(toList(res));
        if (!moveCat) {
          setSubcategories(DEFAULT_MOVE_SUBCATEGORIES);
          return;
        }
        setMoveCategoryId(String(moveCat.id));
        if (moveCat.subcategories?.length > 0) {
          setSubcategories(moveCat.subcategories);
          return;
        }
        billingService
          .getPublicServiceSubcategories(moveCat.id)
          .then((subRes: any) => {
            const subs = toList(subRes);
            setSubcategories(subs.length > 0 ? subs : DEFAULT_MOVE_SUBCATEGORIES);
          })
          .catch(() => setSubcategories(DEFAULT_MOVE_SUBCATEGORIES));
      })
      .catch((err: any) => {
        logger.warn("MoveLanding: failed loading service categories:", err);
        setSubcategories(DEFAULT_MOVE_SUBCATEGORIES);
      });
  }, [reduxCategories, reduxSubcategories]);

  return (
    <div className="move-landing-container min-h-screen flex flex-col">
      <SEO
        title="Get Where You Need to Go - SPACIA MOVE"
        description="Car rental integrated into your SPACIA journey, connecting you with accredited rental companies."
      />

      <MoveHero />
      <MoveSearchBar
        categoryId={moveCategoryId}
        subcategories={subcategories}
        disabled
        disabledNote="Car rental search opens when Move launches in Q4 2026."
      />
      <MoveEcosystem />
      <MoveWhy />
      <MoveVehicles />
      <MoveDurations />
      <MovePickup />
      <MoveTrust />
      <MoveJourney />
      <MoveOnePlatform />
      <MovePartner />
      <MoveComingSoon />
      <FinalCtaCard
        eyebrow="One Platform"
        title="One Platform. Every Part of the Journey."
        description="SPACIA brings flexible workspaces, quality accommodation and curated experiences together so you can spend less time searching and more time doing what matters."
        primaryCta={{ label: "Get Started", onClick: () => history.push("/package-signup") }}
        secondaryCta={{
          label: "Speak to Our Team",
          onClick: () => {
            window.location.href = "mailto:sales@spacia.africa";
          },
        }}
      >
        <PillarPills pills={PILLAR_PILLS} />
      </FinalCtaCard>
    </div>
  );
}
